<template>
  <div class="nuclear-stability-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>N/Z ≈ 1 to 1.5 | 稳定核素分布$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Axes -->
          <line x1="50" y1="200" x2="350" y2="200" stroke="#888" /> <!-- Protons Z -->
          <line x1="50" y1="200" x2="50" y2="40" stroke="#888" />   <!-- Neutrons N -->
          
          <!-- N=Z Line -->
          <line x1="50" y1="200" x2="210" y2="40" stroke="rgba(255,255,255,0.1)" stroke-dasharray="4,4" />
          
          <!-- Stability Belt (Nuclides) -->
          <g v-for="nu in nuclides" :key="nu.id">
             <circle :cx="50 + nu.z*2.5" :cy="200 - nu.n*1.5" r="2" 
                     :fill="nu.stable ? '#2ed573' : (nu.mode === 'beta-' ? '#ff4757' : '#1e3799')" 
                     :opacity="nu.stable ? 1 : 0.4" />
          </g>

          <!-- Highlight Pointer -->
          <circle v-if="activeNu" :cx="50 + activeNu.z*2.5" :cy="200 - activeNu.n*1.5" r="5" fill="none" stroke="#ffd700" stroke-width="1">
             <animate attributeName="r" values="5;10;5" dur="1s" repeatCount="indefinite" />
          </circle>

          <text x="330" y="215" fill="#888" font-size="8">质子数 Z</text>
          <text x="30" y="35" fill="#888" font-size="8" transform="rotate(-90, 30, 35)">中子数 N</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>当前核素: {{ selectedName }}</label>
              <div class="value" :style="{color: activeNu?.stable ? '#2ed573' : '#ff4757'}">
                 {{ activeNu?.stable ? '稳定核素' : '放射性核素 (' + activeNu?.mode + ' 衰变)' }}
              </div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center">
             <label>搜索特定原子序数 Z</label>
             <el-slider v-model="searchZ" :min="1" :max="92" size="mini"></el-slider>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>核稳定性由**强相互作用（引力）**和**库仑力（斥力）**的平衡决定。
           1. **轻核 (Z &lt; 20)**：N/Z 接近 1 时最稳定。
           2. **重核**：由于质子间斥力随 Z² 增长，需要更多的中子（提供额外强力且无库仑力）来维持平衡，导致稳定带偏向 N 轴 (N/Z 达到约 1.5)。
           偏离稳定性带的核素会通过 **Beta 衰变**、**Alpha 衰变**或**自发裂变**向稳定区域回归。这是理解同位素医学应用和恒星演化的钥匙。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NuclearStabilityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      searchZ: 6,
      nuclides: []
    }
  },
  computed: {
    activeNu() {
       return this.nuclides.find(n => n.z === this.searchZ && n.stable) || this.nuclides.find(n => n.z === this.searchZ);
    },
    selectedName() {
       const symbols = { 1:'H', 2:'He', 6:'C', 8:'O', 26:'Fe', 82:'Pb', 92:'U' };
       return `${symbols[this.searchZ] || 'X'} (Z=${this.searchZ})`;
    }
  },
  methods: {
    generateData() {
       const data = [];
       for (let z = 1; z <= 92; z++) {
          // Stable nuclides follow a rough parabola-like path
          let stableN = z * (1 + 0.005 * z); // S-curve approximation
          data.push({ id: `s${z}`, z, n: Math.round(stableN), stable: true });
          // Radioisotopes around them
          data.push({ id: `r1${z}`, z, n: Math.round(stableN) + 5, stable: false, mode: 'beta-' });
          data.push({ id: `r2${z}`, z, n: Math.round(stableN) - 5, stable: false, mode: 'beta+' });
       }
       this.nuclides = data;
    }
  },
  mounted() { this.generateData(); }
}
</script>

<style scoped lang="scss">
.nuclear-stability-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(46, 213, 115, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
